import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import * as Sentry from '@sentry/nextjs';
import type { IUser } from '@teable/sdk';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type { AppContext, AppProps as NextAppProps } from 'next/app';
import App from 'next/app';
import Head from 'next/head';
import { appWithTranslation } from 'next-i18next';
import { DefaultSeo } from 'next-seo';
import { useEffect } from 'react';
import { z } from 'zod';
import { GoogleAnalytics, MicrosoftClarity, Umami } from '@/components/Metrics';
import RouterProgressBar from '@/components/RouterProgress';
import type { IServerEnv } from '@/lib/server-env';
import type { NextPageWithLayout } from '@/lib/type';
import { colors } from '@/themes/colors';
import { getColorsCssVariablesText } from '@/themes/utils';
import nextI18nextConfig from '../../next-i18next.config.js';
import { AppProviders } from '../AppProviders';
import '@glideapps/glide-data-grid/dist/index.css';
dayjs.extend(utc);
dayjs.extend(timezone);
extendZodWithOpenApi(z);

/**
 * Import global styles, global css or polyfills here
 * i.e.: import '@/assets/theme/style.scss'
 */
import '../styles/global.css';

// Krossings brand font 'Jost' is self-hosted via @font-face in styles/global.css (no external font CDN).

// Workaround for https://github.com/zeit/next.js/issues/8592
export type AppProps<T> = NextAppProps<T> & {
  /** Will be defined only is there was an error */
  err?: Error;
};

type AppPropsWithLayout = AppProps<{ user?: IUser; env?: IServerEnv; err?: Error }> & {
  Component: NextPageWithLayout;
};

/**
 * @link https://nextjs.org/docs/advanced-features/custom-app
 */
const MyApp = (appProps: AppPropsWithLayout) => {
  const { Component, err, pageProps } = appProps;
  const { user, env = {}, err: pageErr } = pageProps;
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);
  useEffect(() => {
    Sentry.setUser(user ? { id: user.id, email: user.email } : null);
  }, [user]);

  return (
    <>
      {/* Krossings: Content Hub is a private internal tool; never index. Per-page NextSeo (e.g. LoginPage)
          re-emits robots=index,follow and wins next/head dedup, so a plain DefaultSeo noindex is overridden
          -> use dangerouslySetAllPagesToNoIndex/NoFollow, which forces noindex,nofollow on EVERY page
          regardless of per-page NextSeo. Defence-in-depth alongside the authoritative X-Robots-Tag response
          header set in the NestJS bootstrap. */}
      <DefaultSeo dangerouslySetAllPagesToNoIndex dangerouslySetAllPagesToNoFollow />
      <AppProviders env={env}>
        <Head>
          <meta
            name="viewport"
            content="width=device-width,viewport-fit=cover, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
          />
          <style>{getColorsCssVariablesText(colors)}</style>
        </Head>
        <MicrosoftClarity clarityId={env.microsoftClarityId} user={user} />
        <Umami umamiWebSiteId={env.umamiWebSiteId} umamiUrl={env.umamiUrl} user={user} />
        <GoogleAnalytics gaId={env.gaId} user={user} />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.version="${process.env.NEXT_PUBLIC_BUILD_VERSION ?? 'develop'}";
              window.__TE__=${JSON.stringify(env)};
            `,
          }}
        />
        {/* Workaround for https://github.com/vercel/next.js/issues/8592 */}
        {getLayout(<Component {...pageProps} err={err || pageErr} />, { ...pageProps })}
      </AppProviders>
      {/* Krossings: removed the Guide/joyride tour - its onboarding targets no longer exist (dead), it links
          Teable's API docs, and it polled the DOM every 50ms for up to 5s on every navigation. */}
      <RouterProgressBar />
    </>
  );
};

/**
 * Generally don't enable getInitialProp if you don't need to,
 * all your pages will be served server-side (no static optimizations).
 */

MyApp.getInitialProps = async (appContext: AppContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);
  return { ...appProps };
};

export default appWithTranslation(MyApp, {
  ...nextI18nextConfig,
});
